/**
 * Aurora — 灵动岛顶部控制栏
 * Grid 三列布局 · 真正居中 · 空闲显时钟 · API 三档切换
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import './DynamicIsland.css';

export type IslandState =
  | 'idle' | 'thinking' | 'progress' | 'notification'
  | 'live' | 'quota' | 'error' | 'expanded';

export interface IslandData {
  type: IslandState;
  title?: string;
  body?: string;
  progress?: number;
  quota?: { used: number; total: number; label?: string };
  model?: string;
  duration?: number;
  icon?: string;
}

// ---- 全局事件总线 ----
type Listener = (d: IslandData | null) => void;
const _listeners = new Set<Listener>();
let _current: IslandData | null = null;

export const DynamicIsland = {
  show(data: IslandData) {
    _current = data;
    _listeners.forEach(fn => fn(data));
    if (data.duration && data.duration > 0) {
      setTimeout(() => { if (_current === data) DynamicIsland.hide(); }, data.duration);
    }
  },
  hide() {
    _current = null;
    _listeners.forEach(fn => fn(null));
  },
  subscribe(fn: Listener) {
    _listeners.add(fn);
    fn(_current);
    return () => { _listeners.delete(fn); };
  },
};

// ---- 类型 ----
interface ModelInfo {
  id: string;
  name: string;
  description?: string;
  brand?: string;
  isLocal?: boolean;
  category?: string;
}

interface UsageStats {
  totalTokens: number;
  tokenLimit: number;
  monthlyUsed: number;
  monthlyLimit: number;
  apiCalls: number;
  todayCalls: number;
  avgLatency: number;
}


interface DynamicIslandProps {
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  onSettings?: () => void;
  availableModelIds?: string[];
  userPoints?: number;
}

// ---- Aurora Logo (极光曲线) ----
function AuroraLogoIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="aurora-g1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#64D2FF" />
          <stop offset="50%" stopColor="#5E5CE6" />
          <stop offset="100%" stopColor="#BF5AF2" />
        </linearGradient>
        <linearGradient id="aurora-g2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#30D158" />
          <stop offset="100%" stopColor="#64D2FF" />
        </linearGradient>
      </defs>
      {/* 三条极光曲线 */}
      <path d="M2 16 Q7 8 12 12 Q17 16 22 8" stroke="url(#aurora-g2)" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.7"/>
      <path d="M2 13 Q7 5 12 9 Q17 13 22 5" stroke="url(#aurora-g1)" strokeWidth="2.2" strokeLinecap="round" fill="none"/>
      <path d="M2 19 Q7 14 12 16 Q17 18 22 13" stroke="#64D2FF" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.4"/>
      {/* 顶部光点 */}
      <circle cx="12" cy="9" r="1.5" fill="url(#aurora-g1)" opacity="0.9"/>
    </svg>
  );
}

// ---- SVG 圆环 ----
function QuotaRing({ used, total, size = 68, label, sublabel, color }: {
  used: number; total: number; size?: number;
  label: string; sublabel: string; color: string;
}) {
  const stroke = 4.5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(used / total, 1) : 0;
  const dash = pct * circ;
  const ringColor = pct > 0.85 ? '#FF453A' : pct > 0.65 ? '#FF9F0A' : color;
  return (
    <div className="di-ring-wrap">
      <svg width={size} height={size} style={{ display: 'block' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={ringColor} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ-dash}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: 'stroke-dasharray 0.7s cubic-bezier(0.34,1.56,0.64,1)' }}
        />
        <text x={size/2} y={size/2 - 5} textAnchor="middle" dominantBaseline="middle"
          fill="#fff" fontSize="12" fontWeight="600" fontFamily="system-ui,sans-serif">
          {Math.round(pct * 100)}%
        </text>
        <text x={size/2} y={size/2 + 8} textAnchor="middle" dominantBaseline="middle"
          fill="rgba(255,255,255,0.38)" fontSize="8.5" fontFamily="system-ui,sans-serif">
          {sublabel}
        </text>
      </svg>
      <div className="di-ring-label">{label}</div>
    </div>
  );
}

// ---- 波形 ----
function Waveform({ small }: { small?: boolean }) {
  return (
    <div className={`di-waveform${small ? ' di-waveform--sm' : ''}`}>
      {[0,1,2,3,4].map(i => (
        <div key={i} className="di-waveform__bar" style={{ animationDelay: `${i * 0.08}s` }}/>
      ))}
    </div>
  );
}

// ---- 时钟 ----
function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = now.getHours().toString().padStart(2, '0');
  const mm = now.getMinutes().toString().padStart(2, '0');
  const ss = now.getSeconds().toString().padStart(2, '0');
  return (
    <div className="di__clock">
      <span className="di__clock-time">{hh}<span className="di__clock-colon">:</span>{mm}</span>
    </div>
  );
}


// ---- 主组件 ----
export default function DynamicIslandComponent({ selectedModel, onModelChange, onSettings, availableModelIds, userPoints }: DynamicIslandProps) {
  const [data, setData] = useState<IslandData | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [panelTab, setPanelTab] = useState<'model' | 'usage'>('model');
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => DynamicIsland.subscribe(d => setData(d)), []);

  useEffect(() => {
    fetch('/api/models')
      .then(r => r.json())
      .then(d => { if (d.success && Array.isArray(d.data)) setModels(d.data); })
      .catch(() => {});
  }, []);

  const fetchUsage = useCallback(() => {
    fetch('/api/usage')
      .then(r => r.json())
      .then(d => { if (d.success && d.data) setUsage(d.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchUsage();
    const t = setInterval(fetchUsage, 30_000);
    return () => clearInterval(t);
  }, [fetchUsage]);

  useEffect(() => { if (expanded) fetchUsage(); }, [expanded, fetchUsage]);

  useEffect(() => {
    if (!expanded) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || barRef.current?.contains(t)) return;
      setExpanded(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expanded]);

  const handleModelSelect = useCallback((id: string) => {
    onModelChange?.(id);
    localStorage.setItem('aurora_selected_model', id);
  }, [onModelChange]);


  const type = data?.type;
  const isActive = !!data && type !== 'idle';

  const availableSet = new Set(availableModelIds ?? []);
  const displayModels = models.filter(m => {
    const isText = !m.category || m.category === 'text';
    if (!isText) return false;
    if (availableSet.size > 0) return availableSet.has(m.id);
    return true;
  });

  const modelShort = selectedModel
    ? (selectedModel.length > 12 ? selectedModel.split('-').slice(0, 2).join('-') : selectedModel)
    : '—';

  const fmt = (n: number) => n >= 1_000_000
    ? `${(n/1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${(n/1_000).toFixed(0)}K` : String(n);

  const barClass = [
    'di__bar',
    type === 'thinking'     ? 'di__bar--thinking'     : '',
    type === 'live'         ? 'di__bar--live'         : '',
    type === 'progress'     ? 'di__bar--progress'     : '',
    type === 'error'        ? 'di__bar--error'        : '',
    type === 'notification' ? 'di__bar--notification' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="di">
      {/* ── 顶部控制栏 ── */}
      <div className={barClass} ref={barRef}>
        {type === 'thinking' && <div className="di__scan-line"/>}
        {type === 'progress' && (
          <div className="di__topbar-progress">
            <div className="di__topbar-progress-fill" style={{ width: `${Math.round((data?.progress ?? 0)*100)}%` }}/>
          </div>
        )}

        {/* 左：新 Logo */}
        <div className="di__bar-left">
          <div className="di__logo" onClick={() => setExpanded(p => !p)}>
            <AuroraLogoIcon size={18}/>
          </div>
        </div>

        {/* 中：时钟（空闲）/ 状态（活动） — 绝对居中 */}
        <div className="di__bar-center" onClick={() => setExpanded(p => !p)}>
          {!isActive ? (
            <Clock />
          ) : (
            <div className="di__bar-status">
              {type === 'thinking' && (
                <><div className="di__dots"><span/><span/><span/></div>
                <span className="di__bar-status-text">{data?.title || 'AI 思考中…'}</span></>
              )}
              {type === 'live' && (
                <><Waveform small/><span className="di__bar-status-text">{data?.title || '生成中…'}</span></>
              )}
              {type === 'progress' && (
                <><div className="di__mini-progress">
                  <div className="di__mini-progress-fill" style={{ width: `${Math.round((data?.progress ?? 0)*100)}%` }}/>
                </div>
                <span className="di__bar-status-text">{data?.title}</span>
                <span className="di__bar-progress-pct">{Math.round((data?.progress ?? 0)*100)}%</span></>
              )}
              {type === 'notification' && (
                <><span className="di__bar-done-dot"/><span className="di__bar-status-text">{data?.title}</span></>
              )}
              {type === 'error' && (
                <><span className="di__bar-err-dot"/><span className="di__bar-status-text">{data?.title}</span></>
              )}
            </div>
          )}
        </div>

        {/* 右：API 模式标签 + 模型芯片 + 设置 */}
        <div className="di__bar-right">
          <div className="di__model-chip" onClick={() => { setExpanded(p => !p); setPanelTab('model'); }} title="点击选择模型">
            <span className="di__model-chip-dot"/>
            <span className="di__model-chip-name">{modelShort}</span>
            <svg className="di__model-chip-chevron" width="9" height="9" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {userPoints !== undefined && (
            <div
              className="di__pts-chip"
              onClick={() => { setExpanded(p => !p); setPanelTab('usage'); }}
              title="积分余额，点击查看用量"
            >
              💰 {userPoints.toLocaleString()}
            </div>
          )}
          <button className="di__bar-icon-btn" onClick={onSettings} title="连接中心">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="2" fill="currentColor" opacity="0.7"/>
              <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.2 3.2l1.4 1.4M11.4 11.4l1.4 1.4M3.2 12.8l1.4-1.4M11.4 4.6l1.4-1.4"
                stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── 展开面板 ── */}
      {expanded && (
        <div className="di__panel" ref={panelRef}>
          <div className="di__panel-header">
            <div className="di__panel-logo">
              <AuroraLogoIcon size={14}/>
              <span>控制中心</span>
            </div>
            <button className="di__panel-close" onClick={() => setExpanded(false)}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* AI 状态 */}
          {isActive && (
            <div className="di__panel-status">
              {type === 'thinking' && <><div className="di__dots"><span/><span/><span/></div><span className="di__panel-status-text">{data?.title}</span></>}
              {type === 'live' && <><Waveform small/><span className="di__panel-status-text">{data?.title}</span></>}
              {type === 'progress' && (
                <><div className="di__mini-progress" style={{ width: 80 }}>
                  <div className="di__mini-progress-fill" style={{ width: `${Math.round((data?.progress ?? 0)*100)}%` }}/>
                </div>
                <span className="di__panel-status-text">{data?.title} {Math.round((data?.progress ?? 0)*100)}%</span></>
              )}
              {(type === 'notification' || type === 'error') && (
                <><span style={{ fontSize: 14 }}>{data?.icon || (type === 'error' ? '⚠' : '✓')}</span>
                <span className="di__panel-status-text">{data?.title}</span></>
              )}
            </div>
          )}

          {/* Tab 切换 */}
          <div className="di__panel-tabs">
            {(['model','usage'] as const).map(tab => (
              <button
                key={tab}
                className={`di__panel-tab${panelTab === tab ? ' di__panel-tab--active' : ''}`}
                onClick={() => setPanelTab(tab)}
              >
                {tab === 'model' ? '模型' : '用量'}
              </button>
            ))}
          </div>

          {/* ── Tab: 模型 ── */}
          {panelTab === 'model' && (
            <div className="di__model-list">
              {models.length === 0 && <div className="di-empty">加载中…</div>}
              {displayModels.map(m => (
                <button
                  key={m.id}
                  className={`di__model-item${selectedModel === m.id ? ' di__model-item--active' : ''}`}
                  onClick={() => handleModelSelect(m.id)}
                >
                  <span className="di__model-check">{selectedModel === m.id ? '✓' : ''}</span>
                  <span className="di__model-info">
                    <span className="di__model-name">{m.name || m.id}</span>
                    {m.description && <span className="di__model-desc">{m.description}</span>}
                  </span>
                  {m.isLocal && <span className="di__model-badge">本地</span>}
                </button>
              ))}
            </div>
          )}

          {/* ── Tab: 用量 ── */}
          {panelTab === 'usage' && (<>
            {/* 积分余额卡 */}
            <div className="di__points-card">
              <span className="di__points-icon">💰</span>
              <div className="di__points-body">
                <span className="di__points-val">
                  {userPoints !== undefined ? userPoints.toLocaleString() : '—'}
                </span>
                <span className="di__points-label">积分余额</span>
              </div>
              <span className="di__points-rate">1 RMB = 100 积分</span>
            </div>
            <div className="di-rings">
              <QuotaRing
                used={usage?.monthlyUsed ?? 0} total={usage?.monthlyLimit ?? 500_000}
                label="本月" sublabel={`${fmt(usage?.monthlyUsed ?? 0)}/${fmt(usage?.monthlyLimit ?? 500_000)}`}
                color="#007AFF"
              />
              <QuotaRing
                used={usage?.totalTokens ?? 0} total={usage?.tokenLimit ?? 2_000_000}
                label="累计" sublabel={`${fmt(usage?.totalTokens ?? 0)}/${fmt(usage?.tokenLimit ?? 2_000_000)}`}
                color="#30D158"
              />
            </div>
            <div className="di-usage-meta">
              <span>今日调用 <strong>{usage?.todayCalls ?? 0}</strong> 次</span>
              <span>均延迟 <strong>{usage?.avgLatency ?? 0}</strong> ms</span>
            </div>
          </>)}


          <div className="di__panel-footer">
            <button className="di__footer-btn" onClick={() => { setExpanded(false); onSettings?.(); }}>
              连接中心 →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
